type TimestampLike = {
  seconds?: number;
  nanoseconds?: number;
  _seconds?: number;
  _nanoseconds?: number;
  toMillis?: () => number;
  toDate?: () => Date;
  valueOf?: () => number | string;
};

type VideoLike = { free?: boolean; companies?: unknown[] } | null | undefined;

const getMillis = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  if (value instanceof Date) {
    const millis = value.getTime();
    return Number.isFinite(millis) ? millis : null;
  }

  if (!value || typeof value !== 'object') return null;

  const timestamp = value as TimestampLike;

  if (typeof timestamp.toMillis === 'function') {
    try {
      const millis = timestamp.toMillis();
      if (Number.isFinite(millis)) return millis;
    } catch {
      // Ignore malformed timestamp helpers and keep trying fallbacks.
    }
  }

  if (typeof timestamp.toDate === 'function') {
    try {
      const date = timestamp.toDate();
      if (date instanceof Date) {
        const millis = date.getTime();
        if (Number.isFinite(millis)) return millis;
      }
    } catch {
      // Ignore malformed timestamp helpers and keep trying fallbacks.
    }
  }

  const seconds = typeof timestamp.seconds === 'number' ? timestamp.seconds : timestamp._seconds;
  if (typeof seconds === 'number' && Number.isFinite(seconds)) {
    const nanoseconds = typeof timestamp.nanoseconds === 'number' ? timestamp.nanoseconds : timestamp._nanoseconds;
    const safeNanoseconds = typeof nanoseconds === 'number' && Number.isFinite(nanoseconds) ? nanoseconds : 0;
    return seconds * 1000 + safeNanoseconds / 1_000_000;
  }

  if (typeof timestamp.valueOf === 'function') {
    try {
      const primitive = timestamp.valueOf();
      if (primitive !== (value as any)) {
        const millis = Number(primitive);
        return Number.isFinite(millis) ? millis : null;
      }
    } catch {
      // Ignore malformed timestamp helpers and keep trying fallbacks.
    }
  }

  return null;
};

export const isFreeWeekActive = (staticData: Record<string, unknown> = {}, now = Date.now()) => {
  const start = getMillis(staticData.FREE_WEEK_START);
  const end = getMillis(staticData.FREE_WEEK_END);

  if (start == null || end == null || !Number.isFinite(now)) return false;

  return now >= start && now <= end;
};

export const hasVerifiedCompanyAccess = (video: VideoLike, verifiedCompanyId?: string | null) => {
  if (!verifiedCompanyId || !Array.isArray(video?.companies)) return false;

  return video.companies.some((company) => {
    if (company == null) return false;

    const companyId =
      (typeof company === 'string' && company) ||
      (typeof company === 'object' && typeof (company as { id?: unknown }).id === 'string' && (company as { id: string }).id) ||
      (typeof company === 'object' && typeof (company as { path?: unknown }).path === 'string' && (company as { path: string }).path.split('/').pop());

    return companyId === verifiedCompanyId;
  });
};

export const shouldLockVideoForUser = (
  {
    currentUser,
    subscription,
    video,
    hasCompanyAccess,
  }: {
    currentUser?: { uid?: string } | null;
    subscription?: unknown;
    userData?: unknown;
    video?: VideoLike;
    hasCompanyAccess?: boolean;
  },
  staticData: Record<string, unknown> = {},
  now = Date.now()
) => {
  const isCompanyRestricted = Array.isArray(video?.companies) && video.companies.length > 0;
  const isAuthenticated = typeof currentUser?.uid === 'string' && currentUser.uid.length > 0;

  if (isCompanyRestricted) return !hasCompanyAccess;
  if (video?.free) return false;
  if (subscription) return false;
  if (!isAuthenticated) return true;

  return !isFreeWeekActive(staticData, now);
};
